/**
 * Serialize/deserialize the Setup dialog's compare options + schema filter to
 * a self-contained, versioned JSON file a user can commit to Git alongside
 * the schema it compares (Jeremy Lindsay, W5.1b — Redgate/ApexSQL-profile
 * parity, "Decision D4": a file (+ W5.1c's pin), not a named-profiles
 * manager).
 *
 * Deliberately excludes ALL connection/database identity — the only
 * database-scoped values it ever carries are the schema NAMES inside
 * `schemaFilter`, which the brief explicitly allows ("no connection ids, or
 * any endpoint identity beyond the schema-name filter"). A file carrying a
 * connection id is a file nobody can share, which defeats the whole request.
 *
 * Reused as-is by W5.1c (pinning a configured comparison to the Object
 * Explorer) — keep this module's shape stable rather than dialog-local.
 * W5.1c imports `pickKnownCompareOptionFields` (NOT the whole document
 * builder): a pin must carry the full endpoints descriptor, which this file
 * deliberately never does, so only the options half is shared. See
 * `comparePin.ts`'s `captureComparePinDefinition`.
 */

import { engineSupportsIgnoreOwner } from './ownerIgnoreCapableEngines';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SchemaCompareOptions, SchemaFilter } from '../../core/types/workspaceTypes';
import { applyEngineDefaults, ENGINE_PROPERTY_ALLOWLIST } from './compareDepth';

export const COMPARE_OPTIONS_FILE_KIND = 'jamsql-schema-compare-options';
export const COMPARE_OPTIONS_FILE_VERSION = 1;

/** Suggested extension for the exported file (brief: `.jamsql-compare.json`). */
export const COMPARE_OPTIONS_FILE_EXTENSION = 'jamsql-compare.json';

export type SchemaCompareOptionsFile = {
  $schema: typeof COMPARE_OPTIONS_FILE_KIND;
  version: typeof COMPARE_OPTIONS_FILE_VERSION;
  /** Unified include/exclude schema-name filter. Omitted entirely when there is none. */
  schemaFilter?: SchemaFilter;
  options: SchemaCompareOptions;
};

export type ParsedCompareOptionsFile = {
  schemaFilter?: SchemaFilter;
  options: SchemaCompareOptions;
  /**
   * `ignoredProperties` entries dropped because they aren't in the target
   * engine's property allow-list (`compareDepth.ts`'s
   * `ENGINE_PROPERTY_ALLOWLIST`) — a foreign-engine property (e.g. an Oracle
   * profile's `pctFree` loaded against SQL Server) or an unrecognized key.
   * Empty when `engine` is unknown (nothing to filter against yet) or
   * nothing was dropped.
   */
  droppedIgnoredProperties: string[];
  /**
   * Soporte 9 review round 2 — the file carried `ignoreOwner: true` but the
   * target engine cannot render (or honour) the option, so it was dropped by
   * the same engine gate the checkbox uses (`ownerIgnoreCapableEngines.ts`).
   * Reported for the same reason `droppedIgnoredProperties` exists: Load
   * REPLACES the dialog's options wholesale, and a silent drop means a user
   * re-saving the file would silently degrade the shared profile.
   */
  droppedIgnoreOwner: boolean;
};

export type ParseCompareOptionsFileResult =
  | { success: true; data: ParsedCompareOptionsFile }
  | { success: false; error: string };

/**
 * Fix round 1 (IMPORTANT 1) — the ONLY thing standing between whatever
 * object happens to be sitting in `options` at export time and the file
 * that LEAVES THIS MACHINE. `applyEngineDefaults` (`compareDepth.ts`) does
 * `{...options, ...}` internally, so handing it an unsanitised object would
 * carry through anything riding along on it — including, if some future
 * writer ever attaches one, a `connectionId`/`server`/`database`/`username`/
 * `password`. Not reachable today (nothing currently constructs an
 * `options` object with those keys), but `options` IS rehydrated
 * unsanitised from persisted stores (`SchemaCompareSetupDialog.tsx`'s
 * `setOptions(entry.options)` / `setOptions(saved)`), so reachability
 * depends on what any FUTURE writer puts on that object — and a leak here
 * is unrecoverable, because you cannot recall a file someone has already
 * committed to a shared repo. `parseCompareOptionsFile` (inbound) already
 * whitelists field-by-field via `pickBoolean`; this makes outbound
 * symmetric instead of a blind passthrough.
 *
 * `satisfies Record<keyof SchemaCompareOptions, unknown>` is a compile-time
 * exhaustiveness gate: if `SchemaCompareOptions` gains a field and this
 * object literal isn't updated to list it, the file fails to compile —
 * "missing property" — instead of the new field silently riding along on
 * the next leak.
 *
 * `includeSequences` IS in the list below, and the way it got there is the
 * whole argument for the gate. This module was written on a branch where
 * `SchemaCompareOptions` (workspaceTypes.ts) had no such field; F07 and the
 * sequence-fetch work added one on two other branches. At the round-4
 * integration merge the two arrived together and the `satisfies` line
 * failed to compile — "Property 'includeSequences' is missing" — which is
 * exactly the event this gate exists to cause. Without it the new field
 * would simply have been dropped from every exported file, silently, and
 * nobody would have had a reason to look.
 *
 * The other half was NOT compiler-forced, so it was done by hand in the same
 * change: `parseCompareOptionsFile` builds its options object field-by-field
 * too, via its own `pickBoolean` calls, and has no equivalent gate. Had only
 * this function been updated, the result would have been **export writes it,
 * load drops it, load replaces state** — a `Load` silently resetting
 * `includeSequences` to its default. Any FUTURE field added to
 * `SchemaCompareOptions` lands in the same trap: this function's `satisfies`
 * gate forces the first half; nothing forces the second.
 *
 * **Exported for W5.1c's comparison PIN** (`comparePin.ts`), which serializes
 * its options through this same function rather than defining a second shape.
 *
 * Be precise about why, because the obvious-sounding reason is false: a pin is
 * stored in `connection.json` (`ConnectionMetaInfo`), and **no export path
 * reads that document**. Its entire IPC surface is get / pinObject /
 * unpinObject / reorderPins / updatePin. `metainfo:export` takes
 * `(connectionId, database)` and returns the SEPARATE `DatabaseMetaInfo`
 * document (`{database}.json`) — see `Docs/MetaInfo.md`. Endpoint identity in a
 * pin does not reach a shared artifact today, and any comment claiming
 * otherwise will be disproved by the next reader, who would then be entitled to
 * relax this control.
 *
 * The real reasons this control earns its keep:
 *  1. **One serialization contract.** There is exactly one answer to "which
 *     fields of `SchemaCompareOptions` get persisted outside the tab", shared
 *     by the file and the pin, with one compile-time gate guarding it. Two
 *     hand-maintained field lists would drift.
 *  2. **Defense in depth against a storage change.** `options` is rehydrated
 *     unsanitised from persisted stores, so its key set is only as trustworthy
 *     as every past and future writer. The whitelist fails closed regardless of
 *     what the storage or export surface becomes later — which is the property
 *     you want from a control you cannot retrofit onto data already written.
 *
 * Note the pin uses this pick ALONE, without `applyEngineDefaults` — a pin
 * snapshots what the tab is configured with right now, and materializing engine
 * defaults into it would make an untouched reopened pin read as diverged from
 * itself (`pinDivergence.ts` deep-compares captured-vs-stored).
 */
export function pickKnownCompareOptionFields(options: SchemaCompareOptions): SchemaCompareOptions {
  return {
    ignoreCase: options.ignoreCase,
    ignoreWhitespace: options.ignoreWhitespace,
    formatSqlForComparison: options.formatSqlForComparison,
    includeTables: options.includeTables,
    includeViews: options.includeViews,
    includeProcedures: options.includeProcedures,
    includeFunctions: options.includeFunctions,
    includeSequences: options.includeSequences,
    includeUserTypes: options.includeUserTypes,
    includeSystemSchemas: options.includeSystemSchemas,
    ignoredProperties: options.ignoredProperties ? [...options.ignoredProperties] : undefined,
    ignoreOwner: options.ignoreOwner,
    detailed: options.detailed,
    // R3-2 — the transaction wrap. The ONE field in here that is not a compare
    // input (see its JSDoc in `workspaceTypes.ts`), and carrying it is the
    // point of the move: "and the generated script is all-or-nothing" is part
    // of the profile a team shares in Git, not a per-tab checkbox nobody else
    // can see. `undefined` stays undefined, so a file written on SQL Server
    // does not impose `true` when it is loaded against MySQL — the engine gate
    // resolves the default at read time.
    wrapScriptInTransaction: options.wrapScriptInTransaction,
  } satisfies Record<keyof SchemaCompareOptions, unknown>;
}

/** Same reasoning as `pickKnownCompareOptionFields`, for the (much smaller) filter type. */
function pickSchemaFilterFields(filter: SchemaFilter): SchemaFilter {
  return {
    mode: filter.mode,
    schemas: [...filter.schemas],
  } satisfies Record<keyof SchemaFilter, unknown>;
}

/**
 * Build the file document from the dialog's current state. Materializes
 * `ignoreCase`/`ignoreWhitespace`/`formatSqlForComparison` via the same
 * `applyEngineDefaults` the Setup dialog already snapshots into "Recently
 * compared" (`SchemaCompareSetupDialog.handleOpenCompare`), so the file is
 * self-contained rather than leaning on undefined-means-default. Object-type
 * flags are left as-is (undefined keeps its documented "default true"
 * meaning), matching every other options snapshot in this codebase. Both
 * `options` and `schemaFilter` are whitelisted field-by-field (see
 * `pickKnownCompareOptionFields`) BEFORE anything touches them — this function
 * never spreads a caller-supplied object directly into the outbound file.
 */
export function serializeCompareOptionsFile(
  options: SchemaCompareOptions,
  schemaFilter: SchemaFilter | undefined,
): SchemaCompareOptionsFile {
  const hasFilter = !!schemaFilter && schemaFilter.schemas.length > 0;
  return {
    $schema: COMPARE_OPTIONS_FILE_KIND,
    version: COMPARE_OPTIONS_FILE_VERSION,
    ...(hasFilter ? { schemaFilter: pickSchemaFilterFields(schemaFilter) } : {}),
    options: applyEngineDefaults(pickKnownCompareOptionFields(options)),
  };
}

/** Pretty-printed, git-diff-friendly (2-space indent, trailing newline). */
export function stringifyCompareOptionsFile(file: SchemaCompareOptionsFile): string {
  return `${JSON.stringify(file, null, 2)}\n`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Copies `key` into a fresh object only when the raw value is actually a boolean. */
function pickBoolean(
  raw: Record<string, unknown>,
  key: keyof SchemaCompareOptions,
): Partial<SchemaCompareOptions> {
  const value = raw[key];
  return typeof value === 'boolean' ? { [key]: value } : {};
}

function filterIgnoredProperties(
  raw: unknown,
  engine: DatabaseEngineId | undefined,
): { kept: string[]; dropped: string[] } {
  const strings = Array.isArray(raw) ? raw.filter((p): p is string => typeof p === 'string') : [];
  if (!engine) return { kept: strings, dropped: [] };
  const allowlist = new Set(ENGINE_PROPERTY_ALLOWLIST[engine]);
  return {
    kept: strings.filter((p) => allowlist.has(p)),
    dropped: strings.filter((p) => !allowlist.has(p)),
  };
}

function sanitizeSchemaFilter(raw: unknown): SchemaFilter | undefined {
  if (!isPlainObject(raw)) return undefined;
  if (raw.mode !== 'include' && raw.mode !== 'exclude') return undefined;
  const schemas = Array.isArray(raw.schemas)
    ? raw.schemas.filter((s): s is string => typeof s === 'string')
    : [];
  if (schemas.length === 0) return undefined;
  return { mode: raw.mode, schemas };
}

/**
 * Deserialize = version check + engine-allowlist filtering (brief). Never
 * throws on malformed input — unknown/foreign-engine `ignoredProperties`
 * entries are dropped and reported via `droppedIgnoredProperties`, never a
 * crash; a completely unreadable file returns `success: false` with a
 * message fit to show the user directly.
 */
export function parseCompareOptionsFile(
  raw: string,
  engine: DatabaseEngineId | undefined,
): ParseCompareOptionsFileResult {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { success: false, error: 'That file is not valid JSON.' };
  }

  if (!isPlainObject(json) || json.$schema !== COMPARE_OPTIONS_FILE_KIND) {
    return { success: false, error: "That file isn't a Jam SQL Studio comparison options file." };
  }
  if (typeof json.version !== 'number') {
    return { success: false, error: "That file doesn't declare a valid comparison-options version." };
  }
  if (json.version !== COMPARE_OPTIONS_FILE_VERSION) {
    return {
      success: false,
      error: `This file uses comparison-options format v${json.version}, which this version of Jam SQL Studio doesn't support.`,
    };
  }

  const rawOptions = isPlainObject(json.options) ? json.options : {};
  const { kept: ignoredProperties, dropped: droppedIgnoredProperties } = filterIgnoredProperties(
    rawOptions.ignoredProperties,
    engine,
  );

  // This list has NO compile-time gate — see `pickKnownCompareOptionFields`'s
  // doc comment. Every field added there must be added here by hand, in the
  // same change, or export writes it and load silently drops it.
  // `includeSequences` was added to both at the round-4 integration merge;
  // `wrapScriptInTransaction` at R3-2, where the failure mode would have been
  // sharper than a dropped flag — Load REPLACES the dialog's options wholesale,
  // so a missing line here would silently reset the wrap to its engine default
  // every time a shared profile was loaded, on the one option whose whole
  // reason for being in this file is to travel with it.
  const options: SchemaCompareOptions = {
    ...pickBoolean(rawOptions, 'ignoreCase'),
    ...pickBoolean(rawOptions, 'ignoreWhitespace'),
    ...pickBoolean(rawOptions, 'formatSqlForComparison'),
    ...pickBoolean(rawOptions, 'includeTables'),
    ...pickBoolean(rawOptions, 'includeViews'),
    ...pickBoolean(rawOptions, 'includeProcedures'),
    ...pickBoolean(rawOptions, 'includeFunctions'),
    ...pickBoolean(rawOptions, 'includeSequences'),
    ...pickBoolean(rawOptions, 'includeUserTypes'),
    ...pickBoolean(rawOptions, 'includeSystemSchemas'),
    // Soporte 9 review fix — engine-gated like the collation property above:
    // Load REPLACES the dialog's options wholesale, and an imported
    // `ignoreOwner` on an engine whose Setup dialog never renders the
    // checkbox would be active with no control to see or clear it. With no
    // engine known yet, keep it (same round-trip rule as the rest of the
    // file); the comparer's own engine gate makes it inert regardless.
    ...(engine === undefined || engineSupportsIgnoreOwner(engine)
      ? pickBoolean(rawOptions, 'ignoreOwner')
      : {}),
    ...pickBoolean(rawOptions, 'detailed'),
    ...pickBoolean(rawOptions, 'wrapScriptInTransaction'),
    ignoredProperties,
  };

  return {
    success: true,
    data: {
      options,
      schemaFilter: sanitizeSchemaFilter(json.schemaFilter),
      droppedIgnoredProperties,
      droppedIgnoreOwner:
        engine !== undefined &&
        !engineSupportsIgnoreOwner(engine) &&
        rawOptions.ignoreOwner === true,
    },
  };
}
